import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

export interface LawyerTitle {
  id: string; // The id field from the document (e.g., "1")
  firestoreId: string; // The Firestore document ID (e.g., "37Uuylbf3qnCdoVzQXPV")
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface EventType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlaceType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface Court {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactUsType {
  id: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseStatus {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface CourtType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface FileStatus {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface FileType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinancialTransactionStatus {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface OfficeType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentType {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface Speciality {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubSpeciality {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  createdAt: string;
  updatedAt: string;
}

export interface ForumCategory {
  id: string;
  firestoreId: string;
  name_en: string;
  name_ar: string;
  description: string;
  descriptionAr: string;
  icon: string;
  color: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemPresetData {
  attachmentTypes: AttachmentType[];
  caseStatuses: CaseStatus[];
  caseTypes: CaseType[];
  contactUsTypes: ContactUsType[];
  courtTypes: CourtType[];
  courts: Court[];
  eventTypes: EventType[];
  fileStatuses: FileStatus[];
  fileTypes: FileType[];
  financialTransactionStatuses: FinancialTransactionStatus[];
  forumCategories: ForumCategory[];
  lawyerTitles: LawyerTitle[];
  officeTypes: OfficeType[];
  paymentTypes: PaymentType[];
  placeTypes: PlaceType[];
  specialities: Speciality[];
  subSpecialities: SubSpeciality[];
}

interface SystemPresetContextType {
  presetData: SystemPresetData | null;
  loading: boolean;
  error: string | null;
  loadPresetData: () => Promise<void>;
  clearPresetData: () => void;
  
  // Existing getters
  getLawyerTitleById: (id: string) => LawyerTitle | undefined;
  getLawyerTitleName: (id: string, language: 'en' | 'ar') => string;
  getEventTypeById: (id: string) => EventType | undefined;
  getEventTypeName: (id: string, language: 'en' | 'ar') => string;
  getPlaceTypeById: (id: string) => PlaceType | undefined;
  getPlaceTypeName: (id: string, language: 'en' | 'ar') => string;
  getCourtById: (id: string) => Court | undefined;
  getCourtName: (id: string, language: 'en' | 'ar') => string;
  getContactUsTypeById: (id: string) => ContactUsType | undefined;
  getContactUsTypeName: (id: string, language: 'en' | 'ar') => string;
  
  // New getters for all 16 types
  getAttachmentTypeById: (id: string) => AttachmentType | undefined;
  getAttachmentTypeName: (id: string, language: 'en' | 'ar') => string;
  getCaseStatusById: (id: string) => CaseStatus | undefined;
  getCaseStatusName: (id: string, language: 'en' | 'ar') => string;
  getCaseTypeById: (id: string) => CaseType | undefined;
  getCaseTypeName: (id: string, language: 'en' | 'ar') => string;
  getCourtTypeById: (id: string) => CourtType | undefined;
  getCourtTypeName: (id: string, language: 'en' | 'ar') => string;
  getFileStatusById: (id: string) => FileStatus | undefined;
  getFileStatusName: (id: string, language: 'en' | 'ar') => string;
  getFileTypeById: (id: string) => FileType | undefined;
  getFileTypeName: (id: string, language: 'en' | 'ar') => string;
  getFinancialTransactionStatusById: (id: string) => FinancialTransactionStatus | undefined;
  getFinancialTransactionStatusName: (id: string, language: 'en' | 'ar') => string;
  getOfficeTypeById: (id: string) => OfficeType | undefined;
  getOfficeTypeName: (id: string, language: 'en' | 'ar') => string;
  getPaymentTypeById: (id: string) => PaymentType | undefined;
  getPaymentTypeName: (id: string, language: 'en' | 'ar') => string;
  getSpecialityById: (id: string) => Speciality | undefined;
  getSpecialityName: (id: string, language: 'en' | 'ar') => string;
  getSubSpecialityById: (id: string) => SubSpeciality | undefined;
  getSubSpecialityName: (id: string, language: 'en' | 'ar') => string;
  getForumCategoryById: (id: string) => ForumCategory | undefined;
  getForumCategoryName: (id: string, language: 'en' | 'ar') => string;
}

const SystemPresetContext = createContext<SystemPresetContextType | undefined>(undefined);

export const useSystemPreset = () => {
  const context = useContext(SystemPresetContext);
  if (context === undefined) {
    throw new Error('useSystemPreset must be used within a SystemPresetProvider');
  }
  return context;
};

interface SystemPresetProviderProps {
  children: ReactNode;
}

export const SystemPresetProvider: React.FC<SystemPresetProviderProps> = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [presetData, setPresetData] = useState<SystemPresetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPresetData = async () => {
    try {
      console.log('🚀 Debug - SystemPresetContext: Starting loadPresetData');
      console.log('🆕 Debug - SystemPresetContext: NEW SEQUENTIAL VERSION - This should show SEQUENTIAL STEP logs');
      console.log('🆕 Debug - SystemPresetContext: If you see this, the new code is running');
      console.log('🆕 Debug - SystemPresetContext: VERSION 2.0 - SEQUENTIAL FETCHING - ' + new Date().toISOString());
      console.log('🆕 Debug - SystemPresetContext: This should fetch ALL 16 collections, not just 5');
      setLoading(true);
      setError(null);

      // First, let's discover what collections are available in SystemPresetData
      console.log('🔍 Debug - SystemPresetContext: Discovering available collections...');
      const systemPresetRef = collection(db, 'SystemPresetData');
      const systemPresetSnapshot = await getDocs(systemPresetRef);
      console.log('🔍 Debug - SystemPresetContext: Found SystemPresetData collections:', systemPresetSnapshot.docs.map(doc => doc.id));
      
      // Helper function to fetch and process preset data
      const fetchPresetData = async (
        collectionPath: string,
        transform: (data: any, docId: string) => any
      ): Promise<any[]> => {
        console.log(`🔍 Debug - SystemPresetContext: Fetching ${collectionPath}`);
        const ref = collection(db, 'SystemPresetData', collectionPath, 'items');
        console.log(`🔍 Debug - SystemPresetContext: Collection reference created for ${collectionPath}`);
        const snapshot = await getDocs(ref);
        console.log(`🔍 Debug - SystemPresetContext: Got ${snapshot.size} documents for ${collectionPath}`);
        const items: any[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log(`🔍 Debug - SystemPresetContext: Processing doc ${doc.id} for ${collectionPath}:`, data);
          items.push(transform(data, doc.id));
        });
        
        // Sort by ID (assuming numeric IDs)
        items.sort((a, b) => parseInt(a.id) - parseInt(b.id));
        console.log(`✅ Debug - SystemPresetContext: Processed ${items.length} items for ${collectionPath}`);
        return items;
      };

      // Fetch all 16 preset data collections sequentially with 0.5s delay between each
      console.log('🔍 Debug - SystemPresetContext: Starting SEQUENTIAL fetch of all 16 preset data collections with 0.5s delay');
      console.log('🔍 Debug - SystemPresetContext: This will fetch collections ONE BY ONE, not in parallel');
      
      // Helper function to safely fetch each collection with delay
      const safeFetchPresetDataWithDelay = async (collectionName: string, delayMs: number = 500) => {
        try {
          console.log(`🔍 Debug - SystemPresetContext: Fetching ${collectionName}...`);
          const result = await fetchPresetData(collectionName, (data, docId) => ({
            id: data.id || docId,
            firestoreId: docId,
            name_en: data.name_en || '',
            name_ar: data.name_ar || '',
            createdAt: data.createdAt || '',
            updatedAt: data.updatedAt || ''
          }));
          console.log(`✅ Debug - SystemPresetContext: Successfully loaded ${collectionName} with ${result.length} items`);
          
          // Add delay after successful fetch
          if (delayMs > 0) {
            console.log(`⏳ Debug - SystemPresetContext: Waiting ${delayMs}ms before next collection...`);
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
          
          return result;
        } catch (error) {
          console.error(`❌ Debug - SystemPresetContext: Failed to load ${collectionName}:`, error);
          console.error(`❌ Debug - SystemPresetContext: Error details:`, {
            message: error instanceof Error ? error.message : 'Unknown error',
            code: (error as any)?.code || 'No code',
            collectionName
          });
          
          // Add delay even after failed fetch
          if (delayMs > 0) {
            console.log(`⏳ Debug - SystemPresetContext: Waiting ${delayMs}ms before next collection...`);
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }
          
          return []; // Return empty array for failed collections
        }
      };
      
      // Define the order of collections to fetch
      const collectionsToFetch = [
        'attachmentTypes',
        'caseStatuses', 
        'caseTypes',
        'contactUsTypes',
        'courtTypes',
        'courts',
        'eventTypes',
        'fileStatuses',
        'fileTypes',
        'financialTransactionStatuses',
        'forumCategories',
        'lawyerTitles',
        'officeTypes',
        'paymentTypes',
        'placeTypes',
        'specialities',
        'subSpecialities'
      ];
      
      // Fetch collections sequentially - ONE BY ONE with delays
      console.log('🔄 Debug - SystemPresetContext: Starting SEQUENTIAL loop - will fetch each collection individually');
      console.log('🔄 Debug - SystemPresetContext: About to start the FOR LOOP for sequential fetching');
      console.log('🔄 Debug - SystemPresetContext: Collections to fetch:', collectionsToFetch);
      console.log('🔄 Debug - SystemPresetContext: Collections count:', collectionsToFetch.length);
      console.log('🔄 Debug - SystemPresetContext: This should be 16, not 5!');
      const results: any[] = [];
      console.log('🔄 Debug - SystemPresetContext: Starting FOR LOOP now...');
      for (let i = 0; i < collectionsToFetch.length; i++) {
        const collectionName = collectionsToFetch[i];
        console.log(`🔄 Debug - SystemPresetContext: === SEQUENTIAL STEP ${i + 1}/16 ===`);
        console.log(`🔄 Debug - SystemPresetContext: About to fetch: ${collectionName}`);
        console.log(`🔄 Debug - SystemPresetContext: Previous collections completed: ${i}`);
        
        const result = await safeFetchPresetDataWithDelay(collectionName, 500);
        results.push(result);
        
        console.log(`✅ Debug - SystemPresetContext: Completed ${collectionName}, moving to next collection...`);
        console.log(`📊 Debug - SystemPresetContext: Progress: ${i + 1}/16 collections processed`);
      }
      
      console.log('🎉 Debug - SystemPresetContext: SEQUENTIAL FETCH COMPLETED - All 16 collections processed one by one');
      
      // Destructure results
      const [
        attachmentTypes,
        caseStatuses,
        caseTypes,
        contactUsTypes,
        courtTypes,
        courts,
        eventTypes,
        fileStatuses,
        fileTypes,
        financialTransactionStatuses,
        forumCategories,
        lawyerTitles,
        officeTypes,
        paymentTypes,
        placeTypes,
        specialities,
        subSpecialities
      ] = results;

      // Check what collections are actually available
      const availableCollections = systemPresetSnapshot.docs.map(doc => doc.id);
      console.log('🔍 Debug - SystemPresetContext: Available collections in SystemPresetData:', availableCollections);
      
      // Check if all 17 expected collections exist
      const expectedCollections = [
        'attachmentTypes', 'caseStatuses', 'caseTypes', 'contactUsTypes', 'courtTypes',
        'courts', 'eventTypes', 'fileStatuses', 'fileTypes', 'financialTransactionStatuses',
        'forumCategories', 'lawyerTitles', 'officeTypes', 'paymentTypes', 'placeTypes', 'specialities', 'subSpecialities'
      ];
      
      const missingCollections = expectedCollections.filter(col => !availableCollections.includes(col));
      const extraCollections = availableCollections.filter(col => !expectedCollections.includes(col));
      
      console.log('🔍 Debug - SystemPresetContext: Expected 17 collections:', expectedCollections);
      console.log('🔍 Debug - SystemPresetContext: Missing collections:', missingCollections);
      console.log('🔍 Debug - SystemPresetContext: Extra collections found:', extraCollections);
      
      if (missingCollections.length > 0) {
        console.log('⚠️ Debug - SystemPresetContext: Some expected collections are missing from Firestore');
        console.log('⚠️ Debug - SystemPresetContext: Missing:', missingCollections);
      }
      
      if (extraCollections.length > 0) {
        console.log('ℹ️ Debug - SystemPresetContext: Found additional collections not in expected list:', extraCollections);
      }

      console.log('🔍 Debug - SystemPresetContext: All 16 collections fetched successfully');
      console.log('🔍 Debug - SystemPresetContext: Attachment Types count:', attachmentTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Case Statuses count:', caseStatuses.length);
      console.log('🔍 Debug - SystemPresetContext: Case Types count:', caseTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Contact Us Types count:', contactUsTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Court Types count:', courtTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Courts count:', courts.length);
      console.log('🔍 Debug - SystemPresetContext: Event Types count:', eventTypes.length);
      console.log('🔍 Debug - SystemPresetContext: File Statuses count:', fileStatuses.length);
      console.log('🔍 Debug - SystemPresetContext: File Types count:', fileTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Financial Transaction Statuses count:', financialTransactionStatuses.length);
      console.log('🔍 Debug - SystemPresetContext: Forum Categories count:', forumCategories.length);
      console.log('🔍 Debug - SystemPresetContext: Lawyer Titles count:', lawyerTitles.length);
      console.log('🔍 Debug - SystemPresetContext: Office Types count:', officeTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Payment Types count:', paymentTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Place Types count:', placeTypes.length);
      console.log('🔍 Debug - SystemPresetContext: Specialities count:', specialities.length);
      console.log('🔍 Debug - SystemPresetContext: Sub Specialities count:', subSpecialities.length);

      const newPresetData: SystemPresetData = {
        attachmentTypes: attachmentTypes as AttachmentType[],
        caseStatuses: caseStatuses as CaseStatus[],
        caseTypes: caseTypes as CaseType[],
        contactUsTypes: contactUsTypes as ContactUsType[],
        courtTypes: courtTypes as CourtType[],
        courts: courts as Court[],
        eventTypes: eventTypes as EventType[],
        fileStatuses: fileStatuses as FileStatus[],
        fileTypes: fileTypes as FileType[],
        financialTransactionStatuses: financialTransactionStatuses as FinancialTransactionStatus[],
        forumCategories: forumCategories as ForumCategory[],
        lawyerTitles: lawyerTitles as LawyerTitle[],
        officeTypes: officeTypes as OfficeType[],
        paymentTypes: paymentTypes as PaymentType[],
        placeTypes: placeTypes as PlaceType[],
        specialities: specialities as Speciality[],
        subSpecialities: subSpecialities as SubSpeciality[]
      };

      console.log('✅ Debug - SystemPresetContext: Setting preset data:', newPresetData);
      setPresetData(newPresetData);
    } catch (err) {
      console.error('❌ Error loading system preset data:', err);
      console.error('❌ Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : 'Unknown',
        code: (err as any)?.code || 'No code'
      });
      
      // Handle permission errors gracefully
      if (err instanceof Error && err.message.includes('permissions')) {
        console.warn('⚠️ Debug - Permission denied for system preset data, user may not be authenticated yet');
        setError('Permission denied - please ensure you are logged in');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load preset data');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearPresetData = () => {
    console.log('🧹 Debug - SystemPresetContext: Clearing preset data');
    setPresetData(null);
    setLoading(false);
    setError(null);
    console.log('✅ Debug - SystemPresetContext: Preset data cleared');
  };

  const getLawyerTitleById = (id: string): LawyerTitle | undefined => {
    if (!presetData?.lawyerTitles) return undefined;
    
    // Search by the id field from the document (e.g., "1"), not the Firestore document ID
    // Convert both to string to handle type mismatches
    return presetData.lawyerTitles.find(title => String(title.id) === String(id));
  };

  const getLawyerTitleName = (id: string, language: 'en' | 'ar'): string => {
    const title = getLawyerTitleById(id);

    if (!title) {
      return 'Legal Professional';
    }

    return language === 'ar' ? title.name_ar : title.name_en;
  };

  const getEventTypeById = (id: string): EventType | undefined => {
    if (!presetData?.eventTypes) return undefined;
    return presetData.eventTypes.find(type => String(type.id) === String(id));
  };

  const getEventTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getEventTypeById(id);
    if (!type) {
      return 'Event';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getPlaceTypeById = (id: string): PlaceType | undefined => {
    if (!presetData?.placeTypes) return undefined;
    return presetData.placeTypes.find(type => String(type.id) === String(id));
  };

  const getPlaceTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getPlaceTypeById(id);
    if (!type) {
      return 'Location';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getCourtById = (id: string): Court | undefined => {
    if (!presetData?.courts) return undefined;
    return presetData.courts.find(court => String(court.id) === String(id));
  };

  const getCourtName = (id: string, language: 'en' | 'ar'): string => {
    const court = getCourtById(id);
    if (!court) {
      return 'Court';
    }
    return language === 'ar' ? court.name_ar : court.name_en;
  };

  const getContactUsTypeById = (id: string): ContactUsType | undefined => {
    if (!presetData?.contactUsTypes) return undefined;
    return presetData.contactUsTypes.find(type => String(type.id) === String(id));
  };

  const getContactUsTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getContactUsTypeById(id);
    if (!type) {
      return 'Inquiry';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  // New getter methods for all 16 types
  const getAttachmentTypeById = (id: string): AttachmentType | undefined => {
    if (!presetData?.attachmentTypes) return undefined;
    return presetData.attachmentTypes.find(type => String(type.id) === String(id));
  };

  const getAttachmentTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getAttachmentTypeById(id);
    if (!type) {
      return 'Attachment';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getCaseStatusById = (id: string): CaseStatus | undefined => {
    if (!presetData?.caseStatuses) return undefined;
    return presetData.caseStatuses.find(status => String(status.id) === String(id));
  };

  const getCaseStatusName = (id: string, language: 'en' | 'ar'): string => {
    const status = getCaseStatusById(id);
    if (!status) {
      return 'Status';
    }
    return language === 'ar' ? status.name_ar : status.name_en;
  };

  const getCaseTypeById = (id: string): CaseType | undefined => {
    if (!presetData?.caseTypes) return undefined;
    return presetData.caseTypes.find(type => String(type.id) === String(id));
  };

  const getCaseTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getCaseTypeById(id);
    if (!type) {
      return 'Case Type';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getCourtTypeById = (id: string): CourtType | undefined => {
    if (!presetData?.courtTypes) return undefined;
    return presetData.courtTypes.find(type => String(type.id) === String(id));
  };

  const getCourtTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getCourtTypeById(id);
    if (!type) {
      return 'Court Type';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getFileStatusById = (id: string): FileStatus | undefined => {
    if (!presetData?.fileStatuses) return undefined;
    return presetData.fileStatuses.find(status => String(status.id) === String(id));
  };

  const getFileStatusName = (id: string, language: 'en' | 'ar'): string => {
    const status = getFileStatusById(id);
    if (!status) {
      return 'Status';
    }
    return language === 'ar' ? status.name_ar : status.name_en;
  };

  const getFileTypeById = (id: string): FileType | undefined => {
    if (!presetData?.fileTypes) return undefined;
    return presetData.fileTypes.find(type => String(type.id) === String(id));
  };

  const getFileTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getFileTypeById(id);
    if (!type) {
      return 'File Type';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getFinancialTransactionStatusById = (id: string): FinancialTransactionStatus | undefined => {
    if (!presetData?.financialTransactionStatuses) return undefined;
    return presetData.financialTransactionStatuses.find(status => String(status.id) === String(id));
  };

  const getFinancialTransactionStatusName = (id: string, language: 'en' | 'ar'): string => {
    const status = getFinancialTransactionStatusById(id);
    if (!status) {
      return 'Status';
    }
    return language === 'ar' ? status.name_ar : status.name_en;
  };

  const getOfficeTypeById = (id: string): OfficeType | undefined => {
    if (!presetData?.officeTypes) return undefined;
    return presetData.officeTypes.find(type => String(type.id) === String(id));
  };

  const getOfficeTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getOfficeTypeById(id);
    if (!type) {
      return 'Office Type';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getPaymentTypeById = (id: string): PaymentType | undefined => {
    if (!presetData?.paymentTypes) return undefined;
    return presetData.paymentTypes.find(type => String(type.id) === String(id));
  };

  const getPaymentTypeName = (id: string, language: 'en' | 'ar'): string => {
    const type = getPaymentTypeById(id);
    if (!type) {
      return 'Payment Type';
    }
    return language === 'ar' ? type.name_ar : type.name_en;
  };

  const getSpecialityById = (id: string): Speciality | undefined => {
    if (!presetData?.specialities) return undefined;
    return presetData.specialities.find(speciality => String(speciality.id) === String(id));
  };

  const getSpecialityName = (id: string, language: 'en' | 'ar'): string => {
    const speciality = getSpecialityById(id);
    if (!speciality) {
      return 'Speciality';
    }
    return language === 'ar' ? speciality.name_ar : speciality.name_en;
  };

  const getSubSpecialityById = (id: string): SubSpeciality | undefined => {
    if (!presetData?.subSpecialities) return undefined;
    return presetData.subSpecialities.find(subSpeciality => String(subSpeciality.id) === String(id));
  };

  const getSubSpecialityName = (id: string, language: 'en' | 'ar'): string => {
    const subSpeciality = getSubSpecialityById(id);
    if (!subSpeciality) {
      return 'Sub Speciality';
    }
    return language === 'ar' ? subSpeciality.name_ar : subSpeciality.name_en;
  };

  const getForumCategoryById = (id: string): ForumCategory | undefined => {
    if (!presetData?.forumCategories) return undefined;
    return presetData.forumCategories.find(category => String(category.id) === String(id));
  };

  const getForumCategoryName = (id: string, language: 'en' | 'ar'): string => {
    const category = getForumCategoryById(id);
    if (!category) {
      return 'Forum Category';
    }
    return language === 'ar' ? category.name_ar : category.name_en;
  };

  // Clear preset data when user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      console.log('🧹 Debug - SystemPresetContext: User logged out, clearing preset data');
      clearPresetData();
    }
  }, [isLoggedIn]);

  // Load preset data only when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      console.log('🔍 Debug - Loading system preset data (user is logged in)');
      loadPresetData();
    } else {
      console.log('🔍 Debug - Skipping system preset data load (user not logged in)');
    }
  }, [isLoggedIn]);

  const value: SystemPresetContextType = {
    presetData,
    loading,
    error,
    loadPresetData,
    clearPresetData,
    
    // Existing getters
    getLawyerTitleById,
    getLawyerTitleName,
    getEventTypeById,
    getEventTypeName,
    getPlaceTypeById,
    getPlaceTypeName,
    getCourtById,
    getCourtName,
    getContactUsTypeById,
    getContactUsTypeName,
    
    // New getters for all 16 types
    getAttachmentTypeById,
    getAttachmentTypeName,
    getCaseStatusById,
    getCaseStatusName,
    getCaseTypeById,
    getCaseTypeName,
    getCourtTypeById,
    getCourtTypeName,
    getFileStatusById,
    getFileStatusName,
    getFileTypeById,
    getFileTypeName,
    getFinancialTransactionStatusById,
    getFinancialTransactionStatusName,
    getOfficeTypeById,
    getOfficeTypeName,
    getPaymentTypeById,
    getPaymentTypeName,
    getSpecialityById,
    getSpecialityName,
    getSubSpecialityById,
    getSubSpecialityName,
    getForumCategoryById,
    getForumCategoryName
  };

  return (
    <SystemPresetContext.Provider value={value}>
      {children}
    </SystemPresetContext.Provider>
  );
};
